import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

// NDC operational-profile button.
// - Primary: NDC Red fill, white label, 6px radius, 40px tall.
// - Secondary: NDC Navy fill (used for structural/secondary actions).
// - Outline: transparent with red text + red border.
// - Ghost: transparent, neutral text, subtle hover.
// - Link: red underline.
// - "send" size keeps the chat composer's circular submit affordance.
//
// Brand rule: NDC Red surface area must stay <=10% of any screen, so reserve
// the `primary` variant for genuine primary CTAs.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-[14px] font-semibold leading-none tracking-[0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-ndc-red text-white hover:bg-ndc-red-dark active:bg-ndc-red-dark disabled:opacity-40",
        secondary:
          "bg-ndc-navy text-white hover:bg-ndc-navy-dark active:bg-ndc-navy-dark",
        outline:
          "border border-ndc-red bg-transparent text-ndc-red hover:bg-ndc-red-tint",
        ghost:
          "border border-transparent text-text hover:bg-surface-muted",
        link: "text-ndc-red underline-offset-2 hover:underline",
      },
      size: {
        sm: "h-8 rounded-button px-3",
        md: "h-10 rounded-button px-4",
        icon: "h-10 w-10 rounded-button",
        iconSm: "h-8 w-8 rounded-button",
        send: "h-10 w-10 rounded-button",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { buttonVariants };
